import { callEvents, externalEvents, GroupRoom, roomEvents } from "@closerplatform/closer-sdk";
import { ArtichokeService } from "@fidget/common/services/artichokeService";
import { logger } from "@fidget/common/utils";
import { RoomListener } from "./types";

/**
 * The global event listener that should be implemented by the stores to update their state appropriately.
 * TODO: change WsEventListener => WsEventHandler
 */
export interface WsEventListener {
  /**
   * Unique name to identify and register/unregister listener.
   */
  name: string;
  /**
   * Artichoke events to be consumed by the interested stores.
   */
  meetingScheduled?: (event: externalEvents.MeetingScheduled) => void;
  meetingRescheduled?: (event: externalEvents.MeetingRescheduled) => void;
  meetingCancelled?: (event: externalEvents.MeetingCancelled) => void;
  followerAdded?: (event: externalEvents.FollowerAdded) => void;
  followerRemoved?: (event: externalEvents.FollowerRemoved) => void;
  allFollowersRemoved?: (event: externalEvents.AllFollowersRemoved) => void;
  assigneeChanged?: (event: externalEvents.AssigneeChanged) => void;
  assigneeRemoved?: (event: externalEvents.AssigneeRemoved) => void;
  unreadCountUpdated?: (event: externalEvents.UnreadCountUpdated) => void;
  unreadTotalUpdated?: (event: externalEvents.UnreadTotalUpdated) => void;
  unassignedCountUpdated?: (event: externalEvents.UnassignedCountUpdated) => void;
  agentGroupCreated?: (event: externalEvents.AgentGroupCreated) => void;
  agentGroupUpdated?: (event: externalEvents.AgentGroupUpdated) => void;
  agentGroupDeleted?: (event: externalEvents.AgentGroupDeleted) => void;
  adviserTagGroupsUpdated?: (event: externalEvents.AdviserTagGroupsUpdated) => void;
  callCreated?: (event: callEvents.Created) => void;
  callInvitation?: (event: callEvents.Invited) => void;
  conversationStatusChanged?: (event: externalEvents.ConversationStatusChanged) => void;
  guestProfileUpdated?: (event: externalEvents.GuestProfileUpdated) => void;
  lastAdviserTimestampSet?: (event: externalEvents.LastAdviserTimestampRemoved) => void;
  lastAdviserTimestampRemoved?: (event: externalEvents.LastAdviserTimestampSet) => void;
  notificationUpcomingMeeting?: (event: externalEvents.NotificationUpcomingMeeting) => void;
  presenceUpdated?: (event: externalEvents.PresenceUpdated) => void;
  typingSent?: (event: externalEvents.TypingSent) => void;
  conversationSnoozed?: (event: externalEvents.ConversationSnoozed) => void;
  conversationUnsnoozed?: (event: externalEvents.ConversationUnsnoozed) => void;
  customMessage?: (event: roomEvents.CustomMessageSent) => void;
  unauthorized?: () => void;
  disconnected?: () => void;
  /**
   * Special event type which informs that the connection/re-connection has been established.
   * If store consumes this event, it can initialize/reinitialize its state accordingly.
   */
  connected?: () => void;
}

type EventType = externalEvents.ExternalEvent | callEvents.CallEvent | void;

export class WsGlobalEventsListener {
  private listeners: WsEventListener[] = [];
  private isArtichokeConnected = false;

  constructor(private artichokeService: ArtichokeService) {
    this.artichokeService.onConnected(() => {
      this.isArtichokeConnected = true;
      this.registerHandlers();
      this.invokeHandler("connected");
    });
  }

  addGlobalListener(listener: WsEventListener) {
    this.listeners.push(listener);
    if (listener["connected"] !== undefined) {
      this.invokeOnConnectedHandler(listener);
    }
  }

  removeGlobalListener(name: string) {
    const index = this.listeners.findIndex((listener) => listener.name === name);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  async addRoomListener(roomId: string, listener: RoomListener) {
    await this.artichokeService.onRoomMessage(
      roomId,
      (event) => listener.onRoomMessageReceived(event),
      listener.unsubscribeRoomListeners$
    );
    await this.artichokeService.onRoomCustomMessage(
      roomId,
      (event) => listener.onRoomCustomMessageReceived(event),
      listener.unsubscribeRoomListeners$
    );

    const { onRoomMessageUpdated, onRoomChatMarked, onRoomTypingReceived } = listener;

    if (onRoomMessageUpdated) {
      await this.artichokeService.onRoomMessageUpdated(
        roomId,
        (event) => onRoomMessageUpdated(event),
        listener.unsubscribeRoomListeners$
      );
    }
    if (onRoomTypingReceived) {
      await this.artichokeService.onRoomTyping(
        roomId,
        (event) => onRoomTypingReceived(event),
        listener.unsubscribeRoomListeners$
      );
    }
    if (onRoomChatMarked) {
      await this.artichokeService.onRoomMarked(
        roomId,
        (event) => onRoomChatMarked(event.authorId, event.timestamp),
        listener.unsubscribeRoomListeners$
      );
      await this.artichokeService.onRoomLoad(roomId, (room: GroupRoom) => {
        Object.keys(room.marks).forEach((user) => onRoomChatMarked(user, room.marks[user]!));
      });
    }
  }

  private registerHandlers() {
    // meetings events
    this.artichokeService.onMeetingScheduled((event) =>
      this.invokeHandler("meetingScheduled", event)
    );
    this.artichokeService.onMeetingRescheduled((event) =>
      this.invokeHandler("meetingRescheduled", event)
    );
    this.artichokeService.onMeetingCancelled((event) =>
      this.invokeHandler("meetingCancelled", event)
    );

    // followers events
    this.artichokeService.onFollowerAdded((event) => this.invokeHandler("followerAdded", event));
    this.artichokeService.onFollowerRemoved((event) =>
      this.invokeHandler("followerRemoved", event)
    );
    this.artichokeService.onAllFollowersRemoved((event) =>
      this.invokeHandler("allFollowersRemoved", event)
    );

    // assignments events
    this.artichokeService.onAssigneeChanged((event) =>
      this.invokeHandler("assigneeChanged", event)
    );
    this.artichokeService.onAssigneeRemoved((event) =>
      this.invokeHandler("assigneeRemoved", event)
    );

    // unread counters events
    this.artichokeService.onUnreadCountUpdated((event) =>
      this.invokeHandler("unreadCountUpdated", event)
    );
    this.artichokeService.onUnreadTotalUpdated((event) =>
      this.invokeHandler("unreadTotalUpdated", event)
    );
    this.artichokeService.onUnassignedCountUpdated((event) =>
      this.invokeHandler("unassignedCountUpdated", event)
    );

    this.artichokeService.onAgentGroupCreated((event) =>
      this.invokeHandler("agentGroupCreated", event)
    );
    this.artichokeService.onAgentGroupUpdated((event) =>
      this.invokeHandler("agentGroupUpdated", event)
    );
    this.artichokeService.onAgentGroupDeleted((event) =>
      this.invokeHandler("agentGroupDeleted", event)
    );
    this.artichokeService.onAdviserTagGroupsUpdated((event) =>
      this.invokeHandler("adviserTagGroupsUpdated", event)
    );

    // calls events
    this.artichokeService.onCallCreated((event) => this.invokeHandler("callCreated", event));
    this.artichokeService.onCallInvitation((event) => this.invokeHandler("callInvitation", event));

    // others ...
    this.artichokeService.onUnauthorized(() => this.invokeHandler("unauthorized", undefined));
    this.artichokeService.onDisconnected(() => this.invokeHandler("disconnected", undefined));
    this.artichokeService.onConversationStatusChanged((event) =>
      this.invokeHandler("conversationStatusChanged", event)
    );
    this.artichokeService.onGuestProfileUpdated((event) =>
      this.invokeHandler("guestProfileUpdated", event)
    );
    this.artichokeService.onLastAdviserTimestampRemoved((event) =>
      this.invokeHandler("lastAdviserTimestampRemoved", event)
    );
    this.artichokeService.onLastAdviserTimestampSet((event) =>
      this.invokeHandler("lastAdviserTimestampSet", event)
    );
    this.artichokeService.onNotificationUpcomingMeeting((event) =>
      this.invokeHandler("notificationUpcomingMeeting", event)
    );
    this.artichokeService.onPresenceUpdated((event) =>
      this.invokeHandler("presenceUpdated", event)
    );
    this.artichokeService.onTypingSent((event) => this.invokeHandler("typingSent", event));
    this.artichokeService.onConversationSnoozed((event) =>
      this.invokeHandler("conversationSnoozed", event)
    );
    this.artichokeService.onConversationUnsnoozed((event) =>
      this.invokeHandler("conversationUnsnoozed", event)
    );
    this.artichokeService.onCustomMessage((event) => this.invokeHandler("customMessage", event));
  }

  private invokeHandler<T extends EventType>(name: keyof Omit<WsEventListener, "name">, event?: T) {
    this.listeners.forEach((listener) => {
      const handler = listener[name];
      if (typeof handler === "function") {
        try {
          const typedHandler = handler as (arg?: T) => void;
          typedHandler.call(listener, event);
        } catch (error) {
          logger.error({ message: "Event listener invoke handler error", error });
        }
      }
    });
  }

  /**
   * Invoke "connected" handler when registred after artichoke connect
   */
  private invokeOnConnectedHandler(listener: WsEventListener) {
    const handler = listener["connected"];
    if (this.isArtichokeConnected && handler !== undefined) {
      try {
        handler.call(listener);
      } catch (error) {
        logger.error({ message: "Event listener on connected invoke", error });
      }
    }
  }
}
